import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../providers/AuthProvider';
import { AlertType, useAlert } from '../../providers/AlertProvider';
import { useShoppingCartOverlay } from '../../providers/ShoppingCartOverlayProvider';
import { ICartItem } from '../../lib/fetch/cartItems';
import { SORTING_OPTIONS } from '../../components/ProductsTable/HorizontalFilter';
import ShoppingCartPresentational from './ShoppingCartPresentational';
import {
  cartUtils,
  IAddToCartWithFeedback,
  IDeleteCartItemWithFeedback,
  IUpdateCartWithFeedback,
} from '../../lib/utils';
import { search as callSearchProducts } from '../../lib/fetch/products';
import { WhereToSearch } from '../../lib/enums';
import i18n from '../../i18n';

const ShoppingCart = () => {
  const [{ token, user }, dispatchAuthChange] = useAuth();
  let { addresses } = user!;
  addresses = addresses || [];
  const { cart } = user!;
  const { shipping_address } = cart || {};
  let shippingAddressId = shipping_address && shipping_address.id;
  const [, dispatchAlertChange] = useAlert();
  const [, dispatchCartOverlayChange] = useShoppingCartOverlay();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('SHOPPING_CART');
  const isShippingAddressIdValid = addresses.some(
    ({ id }) => id === shippingAddressId
  );
  const deliveryAddressId = isShippingAddressIdValid
    ? shippingAddressId
    : undefined;
  const lang = i18n.languages[0];

  const onAddToCart = async (search: string) => {
    if (!search) return;
    setLoading(true);
    const { data: axiosData, error } = await callSearchProducts(token!, {
      page: 1,
      pageSize: 2, // We just want to know if there is more than ONE product.
      search,
      sort: SORTING_OPTIONS[0].value,
      where: WhereToSearch.Code,
      lang,
    });
    if (error || !axiosData) {
      setLoading(false);
      return dispatchAlertChange({ open: true });
    }
    const { data = [] } = axiosData;
    if (!data.length) {
      setLoading(false);
      return dispatchAlertChange({
        open: true,
        type: AlertType.Info,
        message: t('PRODUCTS:NO_RESULT_FOR_CODE'),
      });
    }
    if (data.length > 1) {
      setLoading(false);
      return dispatchAlertChange({
        open: true,
        type: AlertType.Info,
        message: t('PRODUCTS:ADD_TO_CART_MULTIPLE_ERROR'),
      });
    }
    dispatchCartOverlayChange({ open: false });
    const [product] = data;
    await cartUtils.addToCartWithFeedback({
      dispatchAlertChange,
      dispatchAuthChange,
      dispatchCartOverlayChange,
      product,
      quantity: 1,
      setAdding: setLoading,
      t,
      token,
      user,
      lang,
    } as IAddToCartWithFeedback);
  };

  const onItemQuantityChange = async (
    cartItem: ICartItem,
    newQuantity: number
  ) => {
    await cartUtils.updateCartWithFeedback({
      cartItem,
      dispatchAlertChange,
      dispatchAuthChange,
      newQuantity,
      setLoading,
      t,
      token,
      user,
      lang,
    } as IUpdateCartWithFeedback);
  };

  const onItemRemove = async (cartItem: ICartItem) => {
    await cartUtils.deleteCartItemWithFeedback({
      cartItem,
      dispatchAlertChange,
      dispatchAuthChange,
      setLoading,
      t,
      token,
      user,
      lang,
    } as IDeleteCartItemWithFeedback);
  };

  return (
    <ShoppingCartPresentational
      cart={cart}
      deliveryAddressId={deliveryAddressId || undefined}
      loading={loading}
      onAddToCart={onAddToCart}
      onItemQuantityChange={onItemQuantityChange}
      onItemRemove={onItemRemove}
    />
  );
};

export default ShoppingCart;
